import { BiddingData, BiddingInfoTypes, FollowupTypes } from 'store/modules/bidding_store/types';

export type BiddingUpdateCategory =
  | 'edital_anexo'
  | 'acompanhamento'
  | 'aviso'
  | 'esclarecimento'
  | 'impugnacao'
  | 'recurso';

export interface BiddingUpdateAttachment {
  id: number;
  filename: string;
  url: string;
  created_at: string | null;
}

export interface BiddingUpdateMessage {
  id: number;
  label: string;
  text: string;
  created_at: string;
}

export interface BiddingUpdate {
  id: string;
  category: BiddingUpdateCategory;
  status: string | null;
  title: string;
  description: string | null;
  messages: BiddingUpdateMessage[];
  author: string | null;
  is_new: boolean;
  created_at: string;
  attachments: BiddingUpdateAttachment[];
  from_client: boolean;
}

export const UPDATE_FILTERS: { key: BiddingUpdateCategory | 'todas'; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'edital_anexo', label: 'Edital/Anexo' },
  { key: 'acompanhamento', label: 'Acompanhamentos' },
  { key: 'aviso', label: 'Avisos' },
  { key: 'esclarecimento', label: 'Esclarecimentos' },
  { key: 'impugnacao', label: 'Impugnações' },
  { key: 'recurso', label: 'Recursos' },
];

export const UPDATE_CATEGORY_ICON: Record<BiddingUpdateCategory, string> = {
  edital_anexo: 'assignment_ind',
  acompanhamento: 'description',
  aviso: 'description',
  esclarecimento: 'description',
  impugnacao: 'description',
  recurso: 'description',
};

export const UPDATE_CATEGORY_LABEL: Record<BiddingUpdateCategory, string> = {
  edital_anexo: 'Edital/Anexo',
  acompanhamento: 'Acompanhamento',
  aviso: 'Aviso',
  esclarecimento: 'Esclarecimento',
  impugnacao: 'Impugnação',
  recurso: 'Recurso',
};

const CATEGORY_BY_TYPE_ID: Record<number, BiddingUpdateCategory> = {
  1: 'esclarecimento',
  2: 'impugnacao',
  3: 'aviso',
  4: 'recurso',
};

export const categoryOf = (info: BiddingInfoTypes): BiddingUpdateCategory =>
  CATEGORY_BY_TYPE_ID[info.type_id] ?? 'aviso';

const fromBiddingInfo = (info: BiddingInfoTypes): BiddingUpdate => {
  const details = info.detail_type_subtypes ?? [];

  return {
    id: `info-${info.id}`,
    category: categoryOf(info),
    status: info.situation ?? null,
    title: info.title,
    description: null,
    messages: details.map((detail) => ({
      id: detail.id,
      label: detail.subtype?.subtype_name ?? '',
      text: detail.description,
      created_at: detail.message_date,
    })),
    author: null,
    is_new: false,
    created_at: info.message_date,
    attachments: details.flatMap((detail) =>
      (detail.urls ?? []).map((item) => ({
        id: item.id,
        filename: item.filename,
        url: item.url,
        created_at: detail.message_date,
      })),
    ),
    from_client: false,
  };
};

const fromFollowup = (followup: FollowupTypes): BiddingUpdate => ({
  id: `followup-${followup.id}`,
  category: 'acompanhamento',
  status: null,
  title: followup.sintese || followup.objeto,
  description: followup.objeto || null,
  messages: [],
  author: null,
  is_new: false,
  created_at: followup.data_fonte,
  attachments: [],
  from_client: false,
});

export const buildBiddingUpdates = (bidding: BiddingData | null): BiddingUpdate[] =>
  [
    ...(bidding?.bidding_info ?? []).map(fromBiddingInfo),
    ...(bidding?.followups ?? []).map(fromFollowup),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
